/*
 * FILE: unified_ui/src/stores/theme.ts
 * PURPOSE: Pinia store for the 2-state theme (light | dark). On first load
 *          reads localStorage `theme`; if missing, falls back to a one-time
 *          OS prefers-color-scheme read. No live OS tracking, no 'system'
 *          mode. Once the user toggles, their explicit choice persists.
 * OWNS: isDark state, class toggle on <html>, localStorage sync.
 * EXPORTS: useThemeStore (isDark, toggle)
 * DOCS: docs/architecture/components/core.md
 */
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

function getInitialTheme(): boolean {
  if (typeof localStorage === 'undefined') {
    return false
  }
  const stored = localStorage.getItem('theme')
  if (stored === 'dark') return true
  if (stored === 'light') return false
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function applyDark(isDark: boolean) {
  if (typeof document === 'undefined') return
  if (isDark) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}

export const useThemeStore = defineStore('theme', () => {
  const isDark = ref<boolean>(getInitialTheme())

  // Apply once on init so the dark class is set before first paint.
  applyDark(isDark.value)

  // Persist + apply whenever the user toggles.
  watch(isDark, (val) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('theme', val ? 'dark' : 'light')
    }
    applyDark(val)
  })

  function toggle() {
    isDark.value = !isDark.value
  }

  return { isDark, toggle }
})
